import { ArrowDownward, ArrowUpward, Close, Remove } from "@mui/icons-material";
import { Box, FormControl, FormLabel, Radio, RadioGroup } from "@mui/joy";
import { RouteDifficulty } from "griptonite-api-client";
import { useCallback } from "react";
import { useFormContext, useController } from "react-hook-form";

import { useCurrentVenueCircuitLookup } from "../../hooks/routes";
import estimateGrade from "../../utils/estimateGrade";
import { getGradeValuesBetween } from "../../utils/grades";

const difficulties = [
  {
    value: -1,
    label: "Easier",
    icon: ArrowDownward,
    color: "var(--joy-palette-success-500)",
  },
  {
    value: 0,
    label: "Normal",
    icon: Remove,
    color: "var(--joy-palette-neutral-900)",
  },
  {
    value: 1,
    label: "Harder",
    icon: ArrowUpward,
    color: "var(--joy-palette-danger-500)",
  },
  {
    value: 2,
    label: "Crux",
    icon: Close,
    color: "var(--joy-palette-neutral-900)",
  },
];

type Props = {
  value: RouteDifficulty | null;
  onChange: (value: RouteDifficulty | null) => void;
};

export default function DifficultySelect({ value, onChange }: Props) {
  return (
    <RadioGroup value={value} sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
      {difficulties.map((difficulty) => (
        <Box
          key={difficulty.value}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            flex: 1,
            padding: 0.5,
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 3,
              pointerEvents: "none",
              display: "flex",
              marginBottom: 0.25,
            }}
          >
            <difficulty.icon sx={{ color: difficulty.color }} />
          </Box>
          <Radio
            onChange={(e) => onChange(Number(e.target.value) as RouteDifficulty)}
            value={difficulty.value}
            label={difficulty.label}
            disableIcon
            overlay
            color="neutral"
            variant={value === difficulty.value ? "soft" : "outlined"}
            slotProps={{
              label: ({ checked }) => ({
                sx: {
                  fontSize: 14,
                  color: checked ? "neutral.800" : "neutral.700",
                },
              }),
              action: ({ checked }) => ({
                sx: {
                  borderRadius: 4,
                  ...(checked
                    ? {
                        border: "1px solid",
                        borderColor: "neutral.600",
                        backgroundColor: "neutral.200",
                        "&:hover": {
                          backgroundColor: "neutral.300",
                        },
                      }
                    : {}),
                },
              }),
            }}
          />
        </Box>
      ))}
    </RadioGroup>
  );
}

export const DifficultyInput = ({
  gradeEstimatedName = "gradeEstimated",
  circuitUuidName = "circuit.uuid",
  name = "circuit.difficulty",
}: {
  gradeEstimatedName?: string;
  circuitUuidName?: string;
  name?: string;
}) => {
  const { control, watch, setValue } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
  });

  const gradeEstimated = watch(gradeEstimatedName) as string | null | undefined;
  const circuitUuid = watch(circuitUuidName) as string | null | undefined;
  const circuit = useCurrentVenueCircuitLookup(circuitUuid || undefined);

  const onChange = useCallback(
    (value: RouteDifficulty | null) => {
      field.onChange(value);
      if (gradeEstimated && circuit) {
        const possibleGrades = getGradeValuesBetween(
          circuit.gradeSystem,
          circuit.minGradeValue,
          circuit.maxGradeValue,
        );
        const estimatedGrade = estimateGrade(possibleGrades, value);
        setValue("gradeValue", estimatedGrade);
      }
    },
    [gradeEstimated, circuit, field, setValue],
  );

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>Difficulty</FormLabel>
      <DifficultySelect value={field.value} onChange={onChange} />
    </FormControl>
  );
};
